
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar,
  Clock,
  MapPin,
  Users,
  BookOpen,
  Target,
  Star,
  CheckCircle,
  ArrowRight,
  Download,
  Microscope,
  Cpu,
  Wrench,
  Palette,
  Calculator,
  Trophy,
  Zap
} from 'lucide-react';

const TermHolidayPrograms = () => {
  const enrichmentPrograms = [
    {
      title: 'Science Explorers',
      description: 'Hands-on science experiments and discovery activities.',
      icon: Microscope,
      features: [
        'Laboratory experiments',
        'Nature exploration',
        'Scientific method',
        'Project presentations'
      ]
    },
    {
      title: 'Engineering Enthusiasts',
      description: 'Build, design, and create innovative solutions.',
      icon: Wrench,
      features: [
        'Structural engineering',
        'Problem solving',
        'Design thinking',
        'Building challenges'
      ]
    },
    {
      title: 'Art Innovators',
      description: 'Digital art and creative multimedia projects.',
      icon: Palette,
      features: [
        'Digital art creation',
        'Multimedia projects',
        'Creative expression',
        'Visual storytelling'
      ]
    },
    {
      title: 'Maths Masters',
      description: 'Advanced mathematical concepts and applications.',
      icon: Calculator,
      features: [
        'Mathematical reasoning',
        'Problem solving',
        'Real-world applications',
        'Logic puzzles'
      ]
    }
  ];

  const bootcampPrograms = [
    {
      title: 'OC Bootcamp',
      duration: '10 days',
      schedule: 'Mon-Fri 9:00 AM - 1:00 PM',
      description: 'Intensive OC exam preparation covering all test components.',
      features: [
        'Comprehensive curriculum coverage',
        'Daily practice tests',
        'Individual progress tracking',
        'Expert teaching strategies',
        'Exam technique development'
      ]
    },
    {
      title: 'Selective Bootcamp',
      duration: '10 days', 
      schedule: 'Mon-Fri 9:00 AM - 1:30 PM',
      description: 'Intensive Selective School entrance exam preparation.',
      features: [
        'Advanced problem solving',
        'Critical thinking development',
        'Comprehensive test practice',
        'Time management skills',
        'Confidence building strategies'
      ]
    }
  ];

  const holidayDates = [
    {
      period: 'Summer Holidays 2024',
      dates: 'January 8-19, 2024',
      programs: 'All enrichment & bootcamp programmes available'
    },
    {
      period: 'Easter Holidays 2024',
      dates: 'April 15-26, 2024',
      programs: '5-day enrichment programmes'
    },
    {
      period: 'Winter Holidays 2024',
      dates: 'July 8-19, 2024',
      programs: 'All programmes available'
    },
    {
      period: 'Spring Holidays 2024',
      dates: 'September 23 - October 4, 2024',
      programs: 'Bootcamp programmes available'
    }
  ];

  const campFeatures = [
    'Small group sizes (max 8 students per class)',
    'Qualified and experienced instructors',
    'All materials and equipment included',
    'Daily progress assessments',
    'Air-conditioned learning spaces',
    'Certificate of completion'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Term & Holiday Programmes | Enrichment & Exam Bootcamps | Schoolola</title>
        <meta name="description" content="Join our Term & Holiday programmes featuring holiday enrichment camps and intensive OC/Selective exam bootcamps. Expert instruction, small groups, comprehensive curriculum." />
        <meta name="keywords" content="term programmes, holiday camps, holiday enrichment, OC bootcamp, selective bootcamp, school holidays, exam preparation" />
      </Helmet>

      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 to-accent-50 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <Badge className="bg-gradient-to-r from-accent to-primary text-white px-6 py-3 text-lg rounded-full">
              Term & Holiday Programmes
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-heading font-bold text-gray-900">
              Inspiring Learning Adventures
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              Choose from our holiday enrichment programmes or intensive exam preparation bootcamps. 
              Designed to accelerate learning and inspire excellence during term time and school holidays.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Tabs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="holiday" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-12 h-auto">
              <TabsTrigger value="holiday" className="flex items-center space-x-2 p-4">
                <Calendar className="h-5 w-5" />
                <span className="font-medium">Holiday Programmes</span>
              </TabsTrigger>
              <TabsTrigger value="bootcamp" className="flex items-center space-x-2 p-4">
                <Trophy className="h-5 w-5" />
                <span className="font-medium">Bootcamps</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="holiday" className="space-y-16">
              {/* Holiday Enrichment Programmes */}
              <div>
                <div className="text-center space-y-4 mb-12">
                  <h2 className="text-3xl font-heading font-bold text-gray-900">
                    Holiday Enrichment Programmes
                  </h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Innovative hands-on learning experiences across Science, Technology, Engineering, Arts & Mathematics
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                  {enrichmentPrograms.map((program, index) => (
                    <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow group">
                      <CardHeader className="bg-gradient-to-r from-primary/10 to-accent/10 pb-4 text-center">
                        <div className="h-12 w-12 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-3">
                          <program.icon className="h-6 w-6 text-white" />
                        </div>
                        <CardTitle className="text-lg text-gray-900">
                          {program.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4 p-4">
                        <p className="text-gray-600 text-sm">{program.description}</p>
                        
                        <ul className="space-y-1">
                          {program.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0 mt-1" />
                              <span className="text-xs text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="pt-3">
                          <Link to="/book-assessment">
                            <Button className="w-full bg-gradient-to-r from-primary to-accent hover:from-accent hover:to-primary text-sm py-2">
                              Register Now
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Holiday Schedule */}
              <div className="bg-gray-50 rounded-3xl p-8">
                <h3 className="text-2xl font-heading font-bold text-gray-900 mb-8 text-center">
                  2024 Holiday Programme Schedule
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {holidayDates.map((holiday, index) => (
                    <Card key={index} className="border-0 shadow-md">
                      <CardContent className="p-6">
                        <h4 className="font-semibold text-lg text-gray-900 mb-2">{holiday.period}</h4>
                        <p className="text-primary font-medium mb-2">{holiday.dates}</p>
                        <p className="text-gray-600 text-sm">{holiday.programs}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="bootcamp" className="space-y-12">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-heading font-bold text-gray-900">
                  Intensive Exam Bootcamps
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Intensive 10-day programmes designed to accelerate exam preparation and boost confidence
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {bootcampPrograms.map((program, index) => (
                  <Card key={index} className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                    <CardHeader className="bg-gradient-to-r from-[#f44321] to-red-600 text-white">
                      <div className="flex items-center space-x-3">
                        <Trophy className="h-8 w-8" />
                        <CardTitle className="text-xl">
                          {program.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6 p-8">
                      <p className="text-gray-600 text-lg">{program.description}</p>
                      
                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <div className="font-medium text-gray-900">Duration</div>
                          <div className="text-gray-600">{program.duration}</div>
                        </div>
                        <div className="space-y-2">
                          <div className="font-medium text-gray-900">Schedule</div>
                          <div className="text-gray-600">{program.schedule}</div>
                        </div>
                      </div>

                      <ul className="space-y-3">
                        {program.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="space-y-3 pt-6">
                        <Link to="/book-assessment">
                          <Button className="w-full bg-[#f44321] hover:bg-red-600 text-lg py-3">
                            Secure Your Place
                            <Zap className="ml-2 h-5 w-5" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
              What's Included in All Programmes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every Schoolola programme includes comprehensive support and premium learning resources
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campFeatures.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white">
              Transform Your Child's Learning Journey
            </h2>
            <p className="text-xl text-primary-100 leading-relaxed">
              Whether you choose our holiday enrichment programmes or intensive exam bootcamps, 
              your child will benefit from expert instruction and accelerated learning outcomes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-assessment">
                <Button size="lg" className="bg-accent hover:bg-accent/90 text-lg px-8 rounded-2xl">
                  Book Free Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="secondary" size="lg" className="text-lg px-8 rounded-2xl">
                  Contact Us Today
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermHolidayPrograms;
